import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import AlignHint from "../../../../parts/photo-compare/AlignHint";
import ControlGroup from "../../../../parts/photo-compare/ControlGroup";
import ModeTabs from "../../../../parts/photo-compare/ModeTabs";
import PairCompareControls from "../../../../parts/photo-compare/PairCompareControls";
import PairCompareStage from "../../../../parts/photo-compare/PairCompareStage";
import usePairCompareState from "../../../../parts/photo-compare/usePairCompareState";
import SvgIcon from "../../../../parts/SvgIcon";

// A photo that can be clicked to open the fullscreen lightbox. Used in the
// side-by-side and single-photo views.
const ExpandablePhoto = ({ src, label, expandLabel, onExpand }) => {
  const open = () => onExpand({ src, label });
  return (
    <button
      type="button"
      className="compare-photo-frame"
      onClick={open}
      aria-label={expandLabel}
    >
      <img src={src} alt={label} />
      <span className="compare-expand-btn" aria-hidden="true">
        <SvgIcon
          src="/img/new-icons/Zoom.svg"
          useCurrentColor
          style={{ width: "1rem", height: "1rem" }}
        />
      </span>
    </button>
  );
};

const CompareProgressPhotosModal = ({
  currentPhotos,
  previousPhotos,
  angles,
  initialAngle,
  previousDate,
  currentDate,
  onClose,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [angle, setAngle] = useState(
    angles.includes(initialAngle) ? initialAngle : angles[0]
  );
  const [mode, setMode] = useState("sbs");
  // Holds { src, label } of the photo shown in the fullscreen lightbox.
  const [expanded, setExpanded] = useState(null);
  // Fade position + per-photo pan/zoom adjustments, so the user can align two
  // shots taken from a different camera position/distance. Alignment is scoped
  // per angle; all of it is session-only (persists across mode/angle switches,
  // cleared when the modal unmounts).
  const compareState = usePairCompareState(angle);

  const previous = previousPhotos?.[angle];
  const current = currentPhotos?.[angle];
  const bothPresent = Boolean(previous && current);
  // When only one side exists we show it large with no comparison modes.
  const singleIsPrevious = Boolean(previous && !current);
  const single = previous || current;

  const handleClose = (e) => {
    e?.preventDefault?.();
    onClose();
  };

  // Keyboard: arrows cycle through the comparable angles, Esc closes. When the
  // lightbox is open it captures Esc first and suspends angle navigation.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (expanded) {
          setExpanded(null);
        } else {
          onClose();
        }
        return;
      }
      if (expanded) return;
      if (angles.length < 2) return;
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        setAngle((prev) => {
          const idx = angles.indexOf(prev);
          const offset = e.key === "ArrowRight" ? 1 : angles.length - 1;
          return angles[(idx + offset) % angles.length];
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [angles, onClose, expanded]);

  const previousLabel = t("questionProgress.previous");
  const currentLabel = t("questionProgress.current");
  const expandLabel = t("questionProgress.expand");

  // Shared by the alignment toolbar and the stage, so both drive the same pair.
  const pair = bothPresent
    ? {
        left: { src: previous, label: previousLabel },
        right: { src: current, label: currentLabel },
      }
    : null;

  return (
    <>
      <Modal
        title={t("questionProgress.compare")}
        className="compare-progress-modal photo-compare"
        onClose={handleClose}
      >
        <div className="body">
          {angles.length > 1 || bothPresent ? (
            <div className="compare-controls">
              {/* Balances the hint at the far right so the panels between them
                  stay centred in the row instead of being pushed left. */}
              {pair && mode !== "sbs" ? (
                <span className="compare-controls-spacer" aria-hidden="true" />
              ) : null}
              {bothPresent ? <ModeTabs mode={mode} onChange={setMode} /> : null}
              {angles.length > 1 ? (
                <ControlGroup panel caption={t("questionProgress.view")}>
                  <div className="angle-tabs">
                    {angles.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={a === angle ? "active" : ""}
                        onClick={() => setAngle(a)}
                      >
                        {/* The label wears its own element so the selected
                            segment can carry both a chip background and
                            gradient lettering — see the CSS. */}
                        <span className="compare-segment-label">
                          {t(`questionProgress.${a}`)}
                        </span>
                      </button>
                    ))}
                  </div>
                </ControlGroup>
              ) : null}
              {pair && mode !== "sbs" ? (
                <>
                  <PairCompareControls
                    stacked
                    mode={mode}
                    left={pair.left}
                    right={pair.right}
                    state={compareState}
                  />
                  <span className="compare-controls-end">
                    <AlignHint pill />
                  </span>
                </>
              ) : null}
            </div>
          ) : null}

          {!bothPresent ? (
            <div className="compare-single">
              <ExpandablePhoto
                src={single}
                label={singleIsPrevious ? previousLabel : currentLabel}
                expandLabel={expandLabel}
                onExpand={setExpanded}
              />
              <div className="compare-single-caption">
                <span>{singleIsPrevious ? previousLabel : currentLabel}</span>
                {(singleIsPrevious ? previousDate : currentDate) ? (
                  <span className="compare-date">
                    {singleIsPrevious ? previousDate : currentDate}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {bothPresent && mode === "sbs" ? (
            <div className="compare-sbs">
              <figure>
                <ExpandablePhoto
                  src={previous}
                  label={previousLabel}
                  expandLabel={expandLabel}
                  onExpand={setExpanded}
                />
                <figcaption>
                  <span>{previousLabel}</span>
                  {previousDate ? (
                    <span className="compare-date">{previousDate}</span>
                  ) : null}
                </figcaption>
              </figure>
              <figure>
                <ExpandablePhoto
                  src={current}
                  label={currentLabel}
                  expandLabel={expandLabel}
                  onExpand={setExpanded}
                />
                <figcaption>
                  <span>{currentLabel}</span>
                  {currentDate ? (
                    <span className="compare-date">{currentDate}</span>
                  ) : null}
                </figcaption>
              </figure>
            </div>
          ) : null}

          {pair && mode !== "sbs" ? (
            <PairCompareStage
              mode={mode}
              left={pair.left}
              right={pair.right}
              state={compareState}
            />
          ) : null}
        </div>
      </Modal>

      {expanded ? (
        // biome-ignore lint/a11y/useKeyWithClickEvents: Esc-to-close is handled in the keydown effect above; this only adds click-the-backdrop-to-close.
        <div
          className="compare-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={expanded.label}
          onClick={(e) => {
            // Close when the dark backdrop itself is clicked, not the image.
            if (e.target === e.currentTarget) setExpanded(null);
          }}
        >
          <button
            type="button"
            className="compare-lightbox-close"
            onClick={() => setExpanded(null)}
            aria-label={t("questionProgress.close")}
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
          <span className="compare-lightbox-label">{expanded.label}</span>
          <img
            className="compare-lightbox-image"
            src={expanded.src}
            alt={expanded.label}
          />
        </div>
      ) : null}
    </>
  );
};

export default CompareProgressPhotosModal;
